import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  CardTypeCallToActions,
  CourseAdditionalFields,
  EnrollmentMessage,
  InstructionsSteps,
  Learning_Type,
  SessionDeliveryType,
  SessionStorageKeys,
  SubscriptionType,
} from '@app/enums/_index';
import {
  CourseInterface,
  DoceboSubscriptionInterface,
  HasPageTitleAndMetaData,
  SubscriptionCourseInterface,
} from '@app/interfaces/_index';
import {
  Enrollment,
  Entry,
  Instructions,
  LearningType,
  LMSSubscription,
  Sessions,
  User,
} from '@app/models/_index';
import {
  CourseService,
  EnrollmentService,
  GoogleAnalyticsService,
  SearchService,
  SubscriptionService,
} from '@app/services/_index';
import {
  calculateExpirationDate,
  compareSwap,
  findUniqueItemById,
  getExpirationTime,
  getKeyByValue,
  getPrimaryUserSubscriptionId,
  getSubscriptionSlugById,
  isDigitalSubscriptionEnrolled,
  isValidSubscription,
  lockBackgroundScrolling,
  logGoogleAnalytics,
  scrollIntoView,
  truncate,
} from '@app/utilities/_index';

import { CommonModule } from '@angular/common';
import { CourseAdapter } from '@app/adapters/_index';
import { ComponentsModule } from '@app/components/components.module';
import { CourseInstructionTypeComponent } from '@app/components/shared/course-instruction-type/course-instruction-type.component';
import { DurationComponent } from '@app/components/shared/duration/duration.component';
import { InstructionType } from '@app/enums/instruction-type.enum';
import { InstructionsItemFactory } from '@app/factories/instructions-item.factory';
import { LearningTypeFactory } from '@app/factories/learning-type.factory';
import { Course } from '@app/models/course.model';
import { PipesModule } from '@app/pipes/pipes.module';
import { UserService } from '@app/services/user/user.service';
import { environment } from '@environments/environment';
import { firstValueFrom, Subscription } from 'rxjs';

@Component({
  selector: 'app-course-detail',
  imports: [
    CommonModule,
    DurationComponent,
    CourseInstructionTypeComponent,
    ComponentsModule,
    PipesModule,
  ],
  templateUrl: './course-detail.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./course-detail.component.scss'],
})
export class CourseDetailComponent implements OnInit, OnDestroy, HasPageTitleAndMetaData {
  // Current user
  currentUser: User;
  subs: Subscription[] = [];
  primaryUserSubscriptionSlugName: string;

  // Primary Course
  course: Course;
  courseId: number;
  slug: string;
  sessionId: number;
  sessionName: string;

  courseAccordionData: Array<any>; // Used to show course data in static accordion

  // Related to Subscriptions
  subscriptionIds: number[] = environment.beyond.subscription_ids;

  // Related Course Siblings taht share the same course Docebo code id
  relatedCourses: Course[] = [];

  // Related Courses map is a map of related courses and their instruction types
  relatedCoursesMap = new Map();

  // User Enrollment
  userEnrollment: Enrollment[] = [];

  // Learning type array model used to get collections
  learningTypes: LearningType[] = [];

  // Instructions
  instructions: Instructions[] = [];

  // Used for Session enrollment error message in upcoming sessions
  sessionEnrollErrorMessage: {};

  // Used for Course enrollment message in course details
  enrollMessage = {};

  EnrollmentMessage = EnrollmentMessage;

  // Subscription Tiles environment Flag
  subscriptionTilesNew = environment.beyond.featureFlags.subscriptionTilesNew;

  // Page State
  isLoading: boolean;
  isUserDataLoading = true;
  toggleSwitch = false; // Default false is the off state or the corresponding off or on Label
  // Tracks whether related-subscriptions and session/enrollment data have each finished loading,
  // so isUserDataLoading (and the CTA it gates) doesn't flip to false before primaryUserSubscriptionSlugName is set.
  private isSubscriptionDataLoaded = false;
  private isSessionDataLoaded = false;

  // Sessions Model
  upcomingSessions: Sessions[] = [];
  pastSessions: Sessions[] = [];

  // Controls to show certification exams
  // When user is logged-in && course is a certification exam && user is not enrolled into the course
  showStudyGuideCta = false;
  showStudyGuideForCertExam = false;
  cardTypeCallToActions = CardTypeCallToActions;

  emptyUser = {} as User;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private metaService: Meta,
    private userService: UserService,
    private courseService: CourseService,
    private courseAdapter: CourseAdapter,
    private enrollmentService: EnrollmentService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private searchService: SearchService,
    private subscriptionService: SubscriptionService,
    private instructionsItemFactory: InstructionsItemFactory,
    private learningTypeFactory: LearningTypeFactory
  ) {}

  /**
   * OnInit
   * Get current user if the user is logged in
   *
   */
  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this.isUserDataLoading = true;
    // Return static learning types from factory
    this.learningTypes = this.learningTypeFactory.getCollection();
    this.instructions = this.instructionsItemFactory.getCollection();

    // TODO: Add Reuse Route Strategy
    this.subs.push(
      this.route.params.subscribe({
        next: (params: Params) => {
          // NEW support for code based routing

          // Accept :code and resolve courseId from cache or search, then short-circuit legacy slug logic
          this.slug = params.slug;
          let courseValue;
          // ---- Try to get cached item based on slug/code
          // TODO: Get this from session storage from full or filtered results
          if (sessionStorage.getItem(this.slug)) {
            // if item is cached
            courseValue = sessionStorage.getItem(this.slug);
            this.courseId = courseValue ? parseInt(courseValue, 10) : -1;
            this.loadCourseById();
            return; // stop here because we handled the code path
          }

          // ---- Try to get item based on cache search results
          // FULL_SEARCH_RESULTS has shape { searchresults: Course[], expiry: ... }
          const full = JSON.parse(
            sessionStorage.getItem(SessionStorageKeys.FULL_SEARCH_RESULTS) || 'null'
          );
          const filtered = JSON.parse(
            sessionStorage.getItem(SessionStorageKeys.FILTERED_SEARCH_RESULTS) || 'null'
          );
          const recommended = JSON.parse(
            sessionStorage.getItem(SessionStorageKeys.RECOMMENDED_FILTERED_SEARCH_RESULTS) || 'null'
          );

          // Collect any arrays we found and ignore nulls
          const pools: any[][] = [full?.searchresults, filtered, recommended].filter(Boolean);

          // Look up by stable course code
          let hit: any = null;
          for (const list of pools) {
            hit = list.find((c) => c?.id === this.slug || c?.code === this.slug);
            if (hit) break;
          }

          // If found in cache, cache the mapping and load
          if (hit?.id) {
            this.courseId = parseInt(hit.id, 10) || -1;
            sessionStorage.setItem(this.slug, String(this.courseId));
            this.loadCourseById();
            return; // stop here because we handled the code path
          }

          // ---- If session storage is empty call getSearchCourses from search service and filter by slug
          const options = {
            query: '',
            roleFilterArray: [],
            productFilterArray: [],
            topicFilterArray: [],
            learningTypeArray: [],
            includeFiltered: true,
          };
          // TODO: Refactor this method. It's the same in search.component.ts
          this.searchService.getSearchCourses(options).then((courses: any[]) => {
            const searchObjectsMap = new Map<string, any>();
            courses.forEach((item) => {
              // get the current item's code, store it a map
              if (!searchObjectsMap.has(item.code)) {
                searchObjectsMap.set(item.code, item);
              } else {
                // If the key exists, get the last course object and update
                // NOTE: Handle Self-study and ILT courses. They keys or the course names
                // are not necessarily in alphabetical order. If the previous code is self study
                // then you need to swap that map key with the one that is alpabetically in the correct order
                // and swap the keep and default object
                // Compare previous and incoming items by course name, specifically for Self-Study and Instructor Led (ILT)
                // Data can be pre-sorted outside of UI (i.e. in Service, Docebo, etc...) or user can click sort descending, so you must
                // compare each time
                compareSwap(searchObjectsMap, item);
              }
            });
            const searchResultsItem = {
              searchresults: courses,
              expiry: getExpirationTime(),
            };
            sessionStorage.setItem(
              SessionStorageKeys.FULL_SEARCH_RESULTS,
              JSON.stringify(searchResultsItem)
            );

            courseValue = courses.filter(
              (course) =>
                course.id == this.slug || course.code == this.slug || course.slug === this.slug
            )[0]?.id;
            this.courseId = courseValue ? parseInt(courseValue, 10) : -1;
            if (this.courseId != -1) {
              sessionStorage.setItem(this.slug, String(this.courseId));
            }
            this.loadCourseById();
          });
        },
        error: (error) => {
          this.isLoading = false;
          console.log('Error loading course', error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Course Detail content completed');
        },
      })
    );
  }

  /**
   * Load Course by CourseId else redirect to Page not found
   */
  loadCourseById(): void {
    if (this.courseId !== -1) {
      this.loadCourse(this.courseId, this.slug);
    } else {
      this.router.navigate(['/pagenotfound']);
    }
  }

  /**
   * Scrolls Into view by given fragment element ID
   * @param fragment element ID to scroll into view
   */
  scrollIntoView(fragment: string): void {
    if (fragment) {
      scrollIntoView(fragment, 'nearest');
    }
  }

  /**
   * Load course by course id
   * @param id - course id number
   * @param slug - course slug string
   */
  loadCourse(id: number, slug: string): void {
    this.subs.push(
      this.courseService.getCourse(id).subscribe({
        next: async (response: CourseInterface) => {
          const course: Course = this.courseAdapter.adapt(response);
          course.slugName = slug;

          // Shows the Course Sessions and Delivery type.
          course.sessions = this.filterCourseSessions(course.sessions);
          course.sessions = this.setDeliveryType(course.sessions);
          course.sessions = this.canEnrollSessions(course.sessions);

          this.course = course;

          const isCertExam =
            this.course?.instructionType === InstructionType.CERTIFICATION_EXAM ||
            this.course?.instructionType === Learning_Type.CERTIFICATION_EXAM;

          this.showStudyGuideForCertExam = isCertExam;

          // wait for current user to load
          const isAuthed = await firstValueFrom(this.userService?.isAuthenticated$);
          if (isAuthed) {
            this.currentUser = await firstValueFrom(this.userService?.doceboUser$);
          }
          // Load Related Subscriptions
          this.loadRelatedSubscriptions();

          // NOTE: Must call course metadata here since it's asynchronous.
          this.loadCourseMetadata();

          // Load Related courses
          this.loadRelatedCourseIds();

          // Load and show sessions
          if (this.currentUser) {
            this.loadEnrolledSessions(this.currentUser);
          } else {
            this.isSessionDataLoaded = true;
            this.updateUserDataLoadingState();
          }

          this.isLoading = false;

          if (this.course?.code && this.course?.id) {
            sessionStorage.setItem(this.course.code, String(this.course.id));
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log('Error: Course could not be found', error);
          this.router.navigateByUrl('pagenotfound');
        },
        complete: () => {
          this.isLoading = false;
          console.log('Load Course completed');
        },
      })
    );
  }

  /**
   * Load Enrolled Sessions on top of the Upcoming Sessions for the current user
   * @param currentUser details of the user currently logged in
   * @param testNow optional Date parameter for testing purposes
   */
  loadEnrolledSessions(currentUser: User, testNow?: Date): void {
    // Reset CTA state first so stale values do not persist between route changes or async updates
    this.showStudyGuideCta = false;
    this.showStudyGuideForCertExam = false;

    if (!currentUser?.id) {
      console.error('Invalid user.');
      this.isSessionDataLoaded = true;
      this.updateUserDataLoadingState();
      return;
    }

    if (!this.course) {
      console.error('Course is unavailable.');
      this.isSessionDataLoaded = true;
      this.updateUserDataLoadingState();
      return;
    }

    // Certification exam flag used for rendering the Study Guide CTA block
    const isCertExam =
      this.course.instructionType === InstructionType.CERTIFICATION_EXAM ||
      this.course.instructionType === Learning_Type.CERTIFICATION_EXAM;

    this.enrollmentService
      .getEnrollments(currentUser.id)
      .then((enrollments: Enrollment[]) => {
        this.userEnrollment = enrollments;

        // Determine if the user is already enrolled in this certification exam course
        const enrolledInThisCourse = this.userEnrollment.some((e) => e.id === this.course?.id);

        // Show Study Guide only for certification exams when the user is not enrolled in this course
        this.showStudyGuideCta = isCertExam && !enrolledInThisCourse;
        this.showStudyGuideForCertExam = isCertExam && !enrolledInThisCourse;

        const enrolledSessions = this.userEnrollment.filter(
          (enrollment) => enrollment.id === this.course?.id
        );

        this.upcomingSessions = this.course.sessions;

        if (enrolledSessions.length > 0) {
          this.course.enrolledSession = [];
          this.pastSessions = [];

          const now = testNow || new Date();

          enrolledSessions.forEach((enrollment) => {
            enrollment.enrolledSessions.forEach((enrolledSession) => {
              const sessionDetail = this.course.sessions.find(
                (session) => session.idSession === enrolledSession.id
              );

              if (sessionDetail) {
                const sessionEndDate = new Date(sessionDetail.endDate);
                if (sessionEndDate < now) {
                  this.pastSessions.push(sessionDetail);
                } else {
                  this.course.enrolledSession.push(sessionDetail);
                }
              }
            });
          });

          this.upcomingSessions = this.course.sessions.filter((session) => {
            return (
              !this.course.enrolledSession.some(
                (enrolledSession) => enrolledSession.idSession === session.idSession
              ) &&
              !this.pastSessions.some((pastSession) => pastSession.idSession === session.idSession)
            );
          });
        } else {
          // Explicitly clear `pastSessions` if no enrollments exist for this course
          this.pastSessions = [];
        }

        this.isSessionDataLoaded = true;
        this.updateUserDataLoadingState();
      })
      .catch((error) => {
        console.error('Error loading enrollments:', error);
        this.isSessionDataLoaded = true;
        this.updateUserDataLoadingState();
      });
  }

  /**
   * Load related course by learning type
   * Find the matching key to the learning type to get the related course id
   * Use the related course id and existing course slug to toggle or reload
   * the course detail page
   * @param learningType - learing type string
   */
  loadRelatedCourse(learningType: string): void {
    if (learningType) {
      const relatedCourses = Array.from(this.relatedCoursesMap.entries());
      const relatedCourseId = getKeyByValue(relatedCourses, learningType);
      this.loadCourse(+relatedCourseId, this.course.slugName);
    }
  }

  /**
   * Load Recommended course by course id
   * @param relatedCourseIds - collection of related course ids
   */
  loadCourses(relatedCourseIds: number[]): void {
    if (this.course) {
      this.relatedCoursesMap.set(this.course.id, this.course.getLearningType());
    }
    if (relatedCourseIds) {
      this.subs.push(
        this.courseService.getCourses(relatedCourseIds).subscribe({
          next: (courses) => {
            for (const item of courses) {
              const response: CourseInterface = item as CourseInterface;
              const course: Course = this.courseAdapter.adapt(response);
              // Remember related courses, but don't load them completely if they are not selected
              this.relatedCourses.push(course);
              // Remember related course id and learning type
              if (!this.relatedCoursesMap.has(course.id)) {
                const learningType = course.getLearningType();
                this.relatedCoursesMap.set(course.id, learningType);
              }
            }
            // Select Self-Study as default Instruction type
            this.toggleSelfStudy();
          },
          error: (error) => {
            this.isLoading = false;
            console.log('Error: Could not load related courses', error);
          },
          complete: () => {
            // Force is loading to stop and complete in case of any unhandled errors
            this.isLoading = false;
            console.log('Course Detail content completed');
          },
        })
      );
    }
  }

  /**
   * Toggle and select Self-Study if the user has selected Self-study in the search filter checkbox
   */
  toggleSelfStudy(): void {
    const selfStudySelected = sessionStorage.getItem(Learning_Type.SELF_STUDY)
      ? sessionStorage.getItem(Learning_Type.SELF_STUDY)
      : 'false';
    if (selfStudySelected && selfStudySelected === 'true') {
      this.toggleSwitch = true;
      this.loadRelatedCourse(Learning_Type.SELF_STUDY);
    }
    sessionStorage.removeItem(Learning_Type.SELF_STUDY);
  }

  /**
   * Return boolean if learning type exists in in the related course maps
   * @param learningType - learning type used to toggle learning type buttons on detail page
   * @returns - boolean if the current learning type is found in the related courses mapping
   */
  hasLearningType(learningType: string): boolean {
    if (learningType) {
      const learningTypes = Array.from(this.relatedCoursesMap.values()) || [];
      if (learningTypes && learningTypes.length > 0) {
        return learningTypes.includes(learningType);
      }
    }
    return false;
  }

  /**
   * Load related courses from cached results
   * Find related courses from cache and re-associate them to the course loaded
   */
  loadRelatedCourseIds(): void {
    // Find related courses
    // NOTE:If it is full results change to flag to be true. This is needed to get the results search results
    // sub object. The filtered results are stored as data results, where as the full results has a expiration date
    // and results.searchResults data which is what you need for full search cache.
    let results;

    const recommendedCoursesSearchResults = JSON.parse(
      sessionStorage.getItem(SessionStorageKeys.RECOMMENDED_FILTERED_SEARCH_RESULTS)
    );
    const filteredSearchResults = JSON.parse(
      sessionStorage.getItem(SessionStorageKeys.FILTERED_SEARCH_RESULTS)
    );
    // NOTE: Handle case where commended courses search results and filtered search results are both not empty.
    // If you click the any of the course links on the recommended courses from subscription detail, it should take
    // results form the recommended filtered search results or combination of the recommended filtered and filtered results.
    if (recommendedCoursesSearchResults && filteredSearchResults) {
      results = recommendedCoursesSearchResults.concat(filteredSearchResults);
    } else if (recommendedCoursesSearchResults) {
      results = recommendedCoursesSearchResults;
    } else if (filteredSearchResults) {
      results = filteredSearchResults;
    }

    if (results) {
      const findCachedCourse: Course = findUniqueItemById(results, this.courseId);
      // Amend related ids to current course. Need to do this because loaded course has more
      // course information then the cached course.
      if (findCachedCourse) {
        const relatedCourseIds = findCachedCourse.relatedCourseIds;
        this.course.relatedCourseIds = relatedCourseIds;
        this.loadCourses(relatedCourseIds);
      } else {
        this.setRelatedCourseIds();
      }
    } else {
      this.setRelatedCourseIds();
    }
  }

  /**
   * Display Course sessions for the date that has not yet passed
   * @param sessions - Session object arrays
   * @returns an array of course sessions
   */
  filterCourseSessions(sessions: Sessions[]): Sessions[] {
    const filteredSessions = [];
    if (sessions && sessions.length > 0) {
      sessions.filter((session: Sessions) => {
        // To check if the Session date has passed
        if (session.startDate && this.hasDatePassed(session.startDate)) {
          filteredSessions.push(session);
        }
      });
    }
    return filteredSessions;
  }

  /**
   * Determine whether user can enroll sessions or not
   * @param sessions - Session object arrays
   * @returns an array of course sessions
   */
  canEnrollSessions(sessions: Sessions[]): Sessions[] {
    sessions.forEach((session) => {
      session.canEnroll = this.hasDatePassed(session.lastSubscriptionDate);
      if (session.canEnroll == false) {
        const errorObj = {
          idSession: session.idSession,
          errorMessage: EnrollmentMessage.REGISTRATION_CLOSED,
        };
        session.sessionEnrollErrorMessage = errorObj.errorMessage;
      }
    });
    return sessions;
  }

  /**
   * Set the Delivery Method type for a session
   * @param sessions - Array of Sessions Object
   * @returns session delivery type
   */
  setDeliveryType(sessions: Sessions[]): Sessions[] {
    if (sessions && sessions.length > 0) {
      sessions.forEach((session: Sessions) => {
        if (session.locations && session.locations.length > 0) {
          session.sessionDeliveryType = SessionDeliveryType.IN_PERSON;
        } else if (
          session.dates &&
          session.dates.length > 0 &&
          session.dates[0].webinarTool &&
          session.dates[0].webinarTool !== 'undefined'
        ) {
          session.sessionDeliveryType = SessionDeliveryType.VIRTUAL;
        }
      });
    }
    return sessions;
  }

  /**
   * TODO: This needs to be an model and adapter
   * This mutates or updates the course fields from the additional fields.
   */
  loadCourseMetadata(): void {
    this.courseAccordionData = this.getAccordionFields();
  }

  /**
   *
   * Return course additional fields data used for accordion
   * @returns accordion fields array by filtering and replacing the html response into a list item
   */
  getAccordionFields(): any[] {
    const fields = [];
    this.course &&
      this.course.additionalFields &&
      this.course.additionalFields.length > 0 &&
      this.course.additionalFields.forEach((field) => {
        // NOTE: Allow == type coercion, DO NOT use === coercion
        // eslint-disable-next-line
        if (field && field.id == CourseAdditionalFields.COURSE_OUTLINE) {
          if (!!field.value) {
            const value = field.value.replace(/style="[^"]*"/g, '');
            fields.push(new Entry(field.title, value, [], true, ''));
          }
        }
      });
    return fields;
  }

  /**
   * Check if the Session Date has paased the current date
   * @param date - current date to check
   * @returns - boolean if date is before current time
   */
  hasDatePassed(date: Date): boolean {
    if (date) {
      const diffDays = calculateExpirationDate(date.toString());
      if (diffDays < 0) {
        return false;
      }
    }
    return true;
  }

  /**
   * Emit toggle switch state on change
   * Default is false (off state or when switch is to the left),
   * true is (on state or when switch is to the right)
   */
  onToggleChange(): void {
    this.toggleSwitch = !this.toggleSwitch;
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: any): void {
    const actionLabel = dataLabel && dataLabel.actionLabel ? dataLabel.actionLabel : dataLabel;
    logGoogleAnalytics(category, action, actionLabel);
    this.googleAnalyticsService.sendEvent(category, action, actionLabel);
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /**
   * Open Request a Voucher Modal for Certification Exams
   * Assume that current user is logged in and the instruction type is
   * Certification Exam, else ignore event.
   * @param clickEvent - handle event emited from the hero banner
   * @param instructionType - course instruction type whic needs to be certification exam
   * @param course - selected Certification Exam to be processed and formatted for email notification.
   */
  openModal(clickEvent: any, instructionType: string, course: Course): void {
    if (this.currentUser && clickEvent && clickEvent.clickButtonFlag) {
      if (course && instructionType === Learning_Type.CERTIFICATION_EXAM) {
        if (document.getElementById('requestForm')) {
          document.getElementById('requestForm').style.display = 'block';

          // NOTE: Fix the background body to be fixed to prevent background scrolling when modal is open
          lockBackgroundScrolling();

          // Pass in course model to modal popup via session storage
          sessionStorage.removeItem(SessionStorageKeys.REQUEST_VOUCHER);
          sessionStorage.setItem(SessionStorageKeys.REQUEST_VOUCHER, JSON.stringify(course));
        }
      }
    }
  }

  /**
   * Enroll and update eLearning course from Enroll to Start state directly
   * Default session id is set to 0, since eLearning does not have a session id.
   * @param clickEvent - handle event emitted from the search result card component
   * @param instructionType - course instruction type must be eLearning
   * @param courseId - pass the courseId for a given course, in this case an eLearning course
   */
  loadEnrollELearning(clickEvent: any, instructionType: string, courseId: number): void {
    // NOTE: Do not auto enroll users if they click on an internal route that is a link (link title or link description)
    // Load enroll should happen only on route types INTERNAL and from a button route type.
    // NOTE: Do not fire enroll for Request a Quote for certificate exams not part of user's
    // current subscription
    if (
      this.currentUser &&
      instructionType &&
      clickEvent &&
      clickEvent.clickButtonFlag &&
      clickEvent.actionLabel &&
      clickEvent.actionLabel !== CardTypeCallToActions.REQUEST_A_QUOTE &&
      (instructionType === Learning_Type.E_LEARNING ||
        instructionType === Learning_Type.SELF_STUDY ||
        instructionType === Learning_Type.QUICK_LEARNING ||
        instructionType === Learning_Type.WEBINAR ||
        instructionType === Learning_Type.WORKSHOP)
    ) {
      this.enrollmentService
        .enrollCourse(this.currentUser.id, courseId)
        .then((response: any) => {
          if (
            response &&
            response.data &&
            response.data.enrolled &&
            response.data.enrolled.length > 0
          ) {
            this.userService.loadEnrollments(this.currentUser);
            this.showStudyGuideForCertExam = false;
          } else {
            // Show enrollment errors in UI if any
            const errorObj = { errorMessage: response.data.errors[0] };
          }
        })
        .catch((error) => {
          const errorObj = { errorMessage: error.message };
        });
    }
  }

  /**
   * Enroll and update Certification course from Enroll to Start state directly
   * Default session id is set to 0, since eLearning does not have a session id.
   * This will enroll the course and will also trigger an API to get the voucher for registering an certification exam through email.
   * @param clickEvent - handle event emitted from the search result card component
   * @param instructionType - course instruction type must be certification exam
   * @param courseId - required courseId for a given certification exam course
   * @param courseName - required course name for a given certificaton exam course
   */
  loadEnrollCertification(
    clickEvent: any,
    instructionType: string,
    courseId: number,
    courseName: string
  ): void {
    // NOTE: Do not auto enroll users if they click on an internal route that is a link (link title or link description)
    // Load enroll should happen only on route types INTERNAL and from a button route type.
    // NOTE: Do not fire enroll for Request a Quote for certificate exams not part of user's
    // current subscription
    if (
      this.currentUser &&
      instructionType &&
      clickEvent &&
      clickEvent.clickButtonFlag &&
      clickEvent.actionLabel &&
      clickEvent.actionLabel !== CardTypeCallToActions.REQUEST_A_QUOTE &&
      instructionType === Learning_Type.CERTIFICATION_EXAM
    ) {
      this.enrollmentService
        .enrollCourse(this.currentUser.id, courseId)
        .then((response: any) => {
          if (
            response &&
            response.data &&
            response.data.enrolled &&
            response.data.enrolled.length > 0
          ) {
            this.enrollmentService
              .createCertificationVoucherForUser(this.currentUser, courseId, courseName)
              .then((response: any) => {
                if (response && response?.voucherCode) {
                  console.log('Final response from certification Voucher catalog page', response);
                  this.enrollMessage = {
                    message:
                      'You have successfully enrolled. Your Genesys Education Exam Voucher will be emailed to you shortly.',
                  };
                }
              })
              .catch((error) => {
                console.log('Error', error);
                const errorObj = {
                  message: error.error.message
                    ? error.error.message
                    : 'Something went wrong generating voucher. please contact beyond@genesys.com for further details.',
                };
                this.enrollMessage = errorObj;
              });
            this.userService.loadEnrollments(this.currentUser);
            this.showStudyGuideForCertExam = false;
          }
        })
        .catch((error) => {
          console.log('Error', error);
          const errorObj = {
            message: error.error.message
              ? error.error.message
              : 'Something went wrong while enrolling a course. Please try after some time.',
          };
          this.enrollMessage = errorObj;
        });
    }
  }

  /**
   * Set selected subscription title and metadata tags
   */
  setTitleAndMetaData(): void {
    // NOTE: Update page title on user dashboard page
    this.titleService.setTitle(`${this.course?.name} | Genesys Education`);
    // NOTE: Update meta tag description for page
    this.metaService.updateTag({
      name: 'description',
      content: truncate(this.course.description, 240) + ' [&hellip;]',
    });
  }

  /**
   * Load Related Subscriptions array for the course.
   */
  loadRelatedSubscriptions(): void {
    let subscriptionsList: DoceboSubscriptionInterface[] = [];
    let result: SubscriptionCourseInterface[] = [];
    const relatedSubscriptionIds: number[] = [];
    this.subscriptionService.getSubscriptions(this.subscriptionIds).subscribe({
      next: (subscriptionItems: DoceboSubscriptionInterface[]) => {
        subscriptionsList = subscriptionItems.filter((nonulls) => nonulls);
        subscriptionsList.forEach((subscriptionItem: DoceboSubscriptionInterface) => {
          result = subscriptionItem.data.courses.filter(
            (course) => course.idCourse === this.courseId
          );
          if (result && result.length > 0) {
            relatedSubscriptionIds.push(subscriptionItem.data.id);
          }
        });
        this.course.relatedSubscriptionIds = relatedSubscriptionIds;
        // Set Primary User Subscription
        this.setPrimaryUserSubscription();
        this.isSubscriptionDataLoaded = true;
        this.updateUserDataLoadingState();
      },
      error: (error) => {
        console.log('Error: Could not load related subscriptions', error);
        this.isSubscriptionDataLoaded = true;
        this.updateUserDataLoadingState();
      },
      complete: () => {
        // Force is loading to stop and complete in case of any unhandled errors
        this.isLoading = false;
        console.log('Load Related Course content completed');
      },
    });
  }

  /**
   * Only clear isUserDataLoading once BOTH related-subscription data (which sets
   * primaryUserSubscriptionSlugName) and session/enrollment data have finished loading.
   * Prevents the "Learn More" CTA from becoming clickable before primaryUserSubscriptionSlugName
   * is populated, which previously caused an intermittent 404 (subscription/undefined).
   */
  private updateUserDataLoadingState(): void {
    if (this.isSubscriptionDataLoaded && this.isSessionDataLoaded) {
      this.isUserDataLoading = false;
    }
  }

  /**
   * Set primary user subscription based on the user active subscription.
   * If the user's default active subscription is found in the related subscriptions, then use
   * this subscription slug for Learn More, instead of the course default parent slug.
   */
  setPrimaryUserSubscription(): void {
    // Current user is not subscribed, so redirect to subscription detail or 'Learn More'
    const userPrimarySubscriptionId = getPrimaryUserSubscriptionId(
      this.currentUser,
      this.course.subscriptionId,
      this.course.relatedSubscriptionIds && this.course.relatedSubscriptionIds.length > 0
        ? this.course.relatedSubscriptionIds
        : [this.course.subscriptionId]
    );
    this.primaryUserSubscriptionSlugName = getSubscriptionSlugById(userPrimarySubscriptionId);
  }

  /**
   * Set Related CourseIds for findCachedCourse for full search results
   */
  setRelatedCourseIds(): void {
    const results = JSON.parse(sessionStorage.getItem(SessionStorageKeys.FULL_SEARCH_RESULTS));
    // Skip any related courses if results is empty or if the related cours doesn't exist for course that
    // is not combined Self-Study and Instructor Led course. Example can be just an eLearning course that has
    // no related course.
    if (results?.searchresults) {
      const findCachedCourse: Course = findUniqueItemById(results.searchresults, this.courseId);
      const relatedCourseIds = findCachedCourse.relatedCourseIds;
      this.course.relatedCourseIds = relatedCourseIds;
      this.loadCourses(relatedCourseIds);
    }
  }

  /**
   * Add Track By Related Subscription item to not redraw or
   * re-render list items that have not changed. This improves performance.
   * @param index - Index of Related Subscription array
   * @param item - Related Subscription list item id
   * @returns index of the Related Subscription array
   */
  trackByRelatedSubscriptionIds(index: number, item: number): number {
    if (item) {
      return item;
    } else {
      return index;
    }
  }

  /**
   * Add Track By Active Subscription item to not redraw or
   * re-render list items that have not changed. This improves performance.
   * @param index - Index of Active Subscription array
   * @param item - Active Subscription list item
   * @returns Active Subscription id
   */
  trackByActiveSubscriptionIds(index: number, item: LMSSubscription): number {
    if (item) {
      return item.id;
    } else {
      return index;
    }
  }

  /**
   * Add Track By Course Session item to not redraw or
   * re-render list items that have not changed. This improves performance.
   * @param index - Index of Course Session array
   * @param item - Course Session list item
   * @returns Session id
   */
  trackByCourseSessionsId(index: number, item: Sessions): number {
    if (item) {
      return item.idSession;
    } else {
      return index;
    }
  }

  /**
   * Return boolean to determine if user has a matching subscription within another set of related subscriptions
   * @param relatedSubscriptionIds - Related subscriptions used to check if the course can appear in 1 to N subscriptions
   * @param subscriptionId - Subscription id used to check if the user has this subscription
   * @param currentUser - If user is signed in, then the transformed type will return a different display type.
   * @returns - boolean indicating a user has a valid subscription
   */
  isValidSubscription(
    relatedSubscriptionIds: number[],
    subscriptionId: number,
    currentUser: User
  ): boolean {
    return isValidSubscription(relatedSubscriptionIds, subscriptionId, currentUser);
  }

  /**
   * Return boolean to determine if user has a Enable Enroll option for Certification Exam Genesys Cloud CX: Professional Certification and
   * Genesys Cloud CX: Developer Certification if the user is enrolled to Genesys Cloud CX Partner Digital Learning or Genesys Cloud CX Partner Digital Plus Learning
   * @param courseId - course Id to verify if it is Certification Exam Genesys Cloud CX: Professional Certification and Genesys Cloud CX: Developer Certification
   * @param user - current user logged in. User contains 1 to N active subscriptions that is registered to that given user
   * @returns - boolean indicating a user has a valid subscription
   */
  isDigitalSubscriptionEnrolled(courseId: number, currentUser: User): boolean {
    return isDigitalSubscriptionEnrolled(courseId, currentUser);
  }

  /**
   * Return an Instructions item by instruction name
   * @param name - instructions name
   * @returns - Instruction model
   */
  getInstructionsByName(name: string): string[] {
    let steps: string[] = [];
    const instructions = this.instructionsItemFactory.getCollectionByInstructionName(name);
    if (instructions) {
      steps = instructions.steps;
    }
    return steps;
  }

  public isEnrolledInCurrentCourse(): boolean {
    if (!this.course?.id || !this.userEnrollment?.length) return false;

    const enrollment = this.userEnrollment.find((e) => e.id === this.course.id);
    return !!(enrollment && enrollment.enrolledSessions && enrollment.enrolledSessions.length >= 0);
  }

  // Component additions

  public get showCertExamStudyGuideButton(): boolean {
    if (!this.currentUser) return false;
    if (this.course?.instructionType !== this.instructionType.CERTIFICATION_EXAM) return false;

    // Only show when signed in and NOT already enrolled in the exam course
    return !this.isDigitalSubscriptionEnrolled(this.courseId, this.currentUser);
  }

  public shouldShowInstructionTypes(): boolean {
    let type: string = this.course?.instructionType || '';
    let typesNotToShow: string[] = [
      InstructionType.E_LEARNING,
      InstructionType.CERTIFICATION_EXAM,
      InstructionType.WEBINAR,
      InstructionType.WORKSHOP,
      InstructionType.QUICK_LEARNING,
    ];
    return typesNotToShow.indexOf(type) < 0;
  }

  public shouldShowStandardCTA(): boolean {
    let type: string = this.course?.instructionType || '';
    let typesToShow: string[] = [
      InstructionType.E_LEARNING,
      InstructionType.SELF_STUDY,
      InstructionType.INSTRUCTOR_LED,
      InstructionType.CERTIFICATION_EXAM,
      InstructionType.WORKSHOP,
      InstructionType.QUICK_LEARNING,
    ];
    return typesToShow.indexOf(type) >= 0;
  }

  /**
   *  SubscriptionType enums based on subscription type
   */
  public get subscriptionType(): typeof SubscriptionType {
    return SubscriptionType;
  }

  /**
   *  InstructionsSteps enums based on instruction steps
   */
  public get instructionSteps(): typeof InstructionsSteps {
    return InstructionsSteps;
  }

  /**
   * Instruction Type enums based on Docebo Instruction types
   */
  public get instructionType(): typeof InstructionType {
    return InstructionType;
  }

  /**
   * Returns true for Boost‑Your‑Knowledge onboarding webinars.
   *  instructionType is WEBINAR AND
   *  the custom additional‑field “Beyond ‑ topic” (id 30
   *  has the value “Onboarding Webinars (Boost Your Knowledge)”.
   */
  public isBYKWebinar(course: Course | null): boolean {
    if (!course) {
      return false;
    }

    const isWebinar =
      course.instructionType === InstructionType.WEBINAR ||
      course.instructionType === Learning_Type.WEBINAR;

    const topicField = course.additionalFields?.find((f) => f.title === 'Beyond - topic');

    const isBYK =
      topicField?.value?.trim().toLowerCase() ===
      'onboarding webinars (boost your knowledge)'.toLowerCase();

    return isWebinar && isBYK;
  }

  public get showSessions(): boolean {
    return (
      this.course?.instructionType === Learning_Type.INSTRUCTOR_LED ||
      this.course?.instructionType === Learning_Type.INSTRUCTOR_LED_VIRTUAL ||
      this.course?.instructionType === Learning_Type.INSTRUCTOR_LED_IN_PERSON ||
      this.course?.instructionType === Learning_Type.WORKSHOP
    );
  }
}
